import type { NavigateFunction } from "react-router-dom";

import apiFetch from "@/app/auth/apiFetch";
import { apiErrorMessage } from "@/lib/billingGrant";

const API_URL = import.meta.env.VITE_API_URL;

// --------------------------------------------------------------------------- //
// Accès offert (billing grants) — backoffice /api/admin/.../billing-grants
// --------------------------------------------------------------------------- //
export type BillingGrantStatus = "active" | "scheduled" | "expired" | "revoked";

export interface BillingGrant {
  id: number;
  organization_id: number;
  tier: string;
  seat_limit: number | null;
  starts_at: string;
  ends_at: string | null;
  unlimited: boolean;
  reason: string;
  granted_by: string;
  created_at: string;
  revoked_at: string | null;
  revoked_by: string | null;
  revoke_reason: string | null;
  applied_at: string | null;
  expired_processed_at: string | null;
  suspended_org: boolean;
  status: BillingGrantStatus;
  // back n'a pas encore appliqué les effets (tier, quotas, suspension).
  effects_pending: boolean;
}

export interface OrganizationBillingSummary {
  id: number;
  name: string;
  is_business: boolean;
  tier: string | null;
  subscription_status: string | null;
  has_stripe_subscription: boolean;
}

export interface OrganizationBillingGrants {
  organization: OrganizationBillingSummary;
  active: BillingGrant | null;
  history: BillingGrant[];
}

export interface BillingGrantMutation {
  grant: BillingGrant;
  effects_applied: boolean;
  effects_error: string | null;
  superseded_grant_ids: number[];
}

export interface BillingGrantCreateInput {
  tier: string;
  seat_limit: number | null;
  duration_days: number | null;
  unlimited: boolean;
  reason: string;
}

export interface BillingGrantExtendInput {
  extend_days: number | null;
  unlimited: boolean;
}

export interface BillingGrantListItem extends BillingGrant {
  organization_name: string;
}

async function readError(res: Response | undefined, fallback: string): Promise<Error> {
  const body = await res?.json?.().catch(() => null);
  return new Error(apiErrorMessage(body, fallback));
}

function jsonInit(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

export async function getOrganizationBillingGrants(
  organizationId: number,
  navigate: NavigateFunction
): Promise<OrganizationBillingGrants> {
  const res = await apiFetch(
    `${API_URL}/admin/organizations/${organizationId}/billing-grants`,
    { method: "GET" },
    navigate
  );
  if (!res?.ok) throw await readError(res, "Chargement de l'accès offert impossible");
  return res.json();
}

export async function createBillingGrant(
  organizationId: number,
  input: BillingGrantCreateInput,
  navigate: NavigateFunction
): Promise<BillingGrantMutation> {
  const res = await apiFetch(
    `${API_URL}/admin/organizations/${organizationId}/billing-grants`,
    jsonInit("POST", input),
    navigate
  );
  if (!res?.ok) throw await readError(res, "Création de l'accès offert impossible");
  return res.json();
}

export async function extendBillingGrant(
  organizationId: number,
  grantId: number,
  input: BillingGrantExtendInput,
  navigate: NavigateFunction
): Promise<BillingGrantMutation> {
  const res = await apiFetch(
    `${API_URL}/admin/organizations/${organizationId}/billing-grants/${grantId}`,
    jsonInit("PATCH", input),
    navigate
  );
  if (!res?.ok) throw await readError(res, "Prolongation impossible");
  return res.json();
}

export async function revokeBillingGrant(
  organizationId: number,
  grantId: number,
  reason: string,
  navigate: NavigateFunction
): Promise<BillingGrantMutation> {
  const res = await apiFetch(
    `${API_URL}/admin/organizations/${organizationId}/billing-grants/${grantId}`,
    jsonInit("DELETE", { reason }),
    navigate
  );
  if (!res?.ok) throw await readError(res, "Révocation impossible");
  return res.json();
}

export async function listBillingGrants(
  navigate: NavigateFunction,
  params: { expiringWithinDays?: number } = {}
): Promise<{ items: BillingGrantListItem[]; total: number }> {
  const qs =
    params.expiringWithinDays !== undefined
      ? `?expiring_within_days=${params.expiringWithinDays}`
      : "";
  const res = await apiFetch(`${API_URL}/admin/billing-grants${qs}`, { method: "GET" }, navigate);
  if (!res?.ok) throw await readError(res, "Chargement des accès offerts impossible");
  return res.json();
}
